import { useEffect, useState } from 'react'

export interface FetchError {
  message: string
  data: unknown
  status?: number
}

interface GetState<M> {
  data: M | null
  loading: boolean
  error: FetchError | null
}

// 204 has no body; JSON is parsed when the server says it's JSON, anything else comes back as text
async function parseBody(response: Response) {
  if (response.status === 204) {
    return { data: undefined, parseError: false }
  }
  if ((response.headers.get('content-type') || '').includes('application/json')) {
    try {
      return { data: await response.json(), parseError: false }
    } catch (e) {
      return { data: (e as Error).message, parseError: true }
    }
  }
  return { data: await response.text(), parseError: false }
}

/**
 * GETs `path` (relative to the app's origin, with cookies) and refetches whenever `path` changes.
 * Errors keep the shape restful-react's <Get> produced, which Error and ErrorAnalytics rely on.
 */
export function useGet<M>(path: string): GetState<M> {
  const [state, setState] = useState<GetState<M>>({
    data: null,
    loading: true,
    error: null
  })

  useEffect(() => {
    const controller = new AbortController()
    setState(prev => ({ ...prev, loading: true, error: null }))

    const load = async () => {
      try {
        const response = await fetch(path, {
          credentials: 'include',
          signal: controller.signal
        })
        const { data, parseError } = await parseBody(response)
        if (controller.signal.aborted) return

        if (!response.ok || parseError) {
          setState(prev => ({
            ...prev,
            loading: false,
            error: {
              message: `Failed to fetch: ${response.status} ${response.statusText}${
                parseError ? ` - ${data}` : ''
              }`,
              data,
              status: response.status
            }
          }))
          return
        }

        setState({ data: data as M, loading: false, error: null })
      } catch (e) {
        if (controller.signal.aborted) return
        setState(prev => ({
          ...prev,
          loading: false,
          error: { message: `Failed to fetch: ${(e as Error).message}`, data: e }
        }))
      }
    }

    load()
    return () => controller.abort()
  }, [path])

  return state
}
